/**
 * v2.7.65+E126 · Navigation Action Sheet
 * ═════════════════════════════════════════════════════════════════════
 * Dropdown next to the meter that offers deep-links to the driver's
 * preferred navigation app — Google Maps, Apple Maps, or Waze — for
 * either the PICKUP or the DROPOFF coordinates of the active ride.
 *
 * Turn-by-turn is offloaded entirely to the native app, so we don't
 * consume any Google Maps SDK / API credits ourselves.
 *
 * Props
 * ─────
 *   pickup       { lat, lng, address }   optional
 *   destination  { lat, lng, address }   optional
 *   compact      boolean                 render as a small icon button (default false)
 */
import { useState } from "react";
import { Navigation, ChevronDown, MapPin, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const buildLinks = (coords, address) => {
  if (!coords || typeof coords.lat !== "number" || typeof coords.lng !== "number") return null;
  const q = `${coords.lat},${coords.lng}`;
  const encoded = encodeURIComponent(address || q);
  return {
    google: `https://www.google.com/maps/dir/?api=1&destination=${q}${address ? `&destination_place=${encoded}` : ""}`,
    apple:  `http://maps.apple.com/?daddr=${q}`,
    waze:   `https://waze.com/ul?ll=${q}&navigate=yes`,
  };
};

function SheetGroup({ title, Icon, coords, address, tag }) {
  const links = buildLinks(coords, address);
  if (!links) return null;
  return (
    <>
      <DropdownMenuLabel className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 dark:text-zinc-500">
        <Icon className="h-3 w-3"/>{title}
      </DropdownMenuLabel>
      {address && (
        <div className="px-2 pb-1 text-[11px] text-slate-600 dark:text-zinc-400 truncate" title={address}>
          {address}
        </div>
      )}
      <DropdownMenuItem asChild data-testid={`nav-open-google-${tag}`}>
        <a href={links.google} target="_blank" rel="noreferrer" className="w-full">
          <span className="inline-block h-2 w-2 rounded-full bg-blue-500 mr-2"/>Google Maps
        </a>
      </DropdownMenuItem>
      <DropdownMenuItem asChild data-testid={`nav-open-apple-${tag}`}>
        <a href={links.apple} target="_blank" rel="noreferrer" className="w-full">
          <span className="inline-block h-2 w-2 rounded-full bg-slate-500 mr-2"/>Apple Maps
        </a>
      </DropdownMenuItem>
      <DropdownMenuItem asChild data-testid={`nav-open-waze-${tag}`}>
        <a href={links.waze} target="_blank" rel="noreferrer" className="w-full">
          <span className="inline-block h-2 w-2 rounded-full bg-cyan-500 mr-2"/>Waze
        </a>
      </DropdownMenuItem>
    </>
  );
}

export default function NavigationActionSheet({ pickup, destination, compact = false }) {
  const [open, setOpen] = useState(false);
  const hasAny = !!pickup?.lat || !!destination?.lat;
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant={compact ? "outline" : "default"}
          size={compact ? "icon" : "sm"}
          disabled={!hasAny}
          data-testid="nav-action-sheet-trigger"
          className={compact
            ? "h-9 w-9 border-amber-400 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
            : "bg-amber-500 hover:bg-amber-600 text-black font-bold gap-1.5"}
          aria-label="Open navigation options"
        >
          <Navigation className={compact ? "h-4 w-4" : "h-4 w-4"}/>
          {!compact && (
            <>
              <span>Navigate</span>
              <ChevronDown className="h-3 w-3 opacity-70"/>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64"
        data-testid="nav-action-sheet-content"
      >
        {!hasAny && (
          <div className="px-3 py-4 text-xs text-slate-500 dark:text-zinc-500">
            No active-ride coordinates available.
          </div>
        )}
        <SheetGroup
          title="Pickup"
          Icon={MapPin}
          coords={pickup}
          address={pickup?.address}
          tag="pickup"
        />
        {pickup?.lat && destination?.lat && <DropdownMenuSeparator/>}
        <SheetGroup
          title="Dropoff"
          Icon={Flag}
          coords={destination}
          address={destination?.address}
          tag="dropoff"
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
